import { useEffect, useRef, useState } from 'react';
import YouTube, { type YouTubeProps, type YouTubePlayer as YTPlayer } from 'react-youtube';
import type { LiveSession, UpdateLiveSessionData } from '@/types';

interface YouTubePlayerProps {
    videoId: string;
    session: LiveSession;
    isAdmin: boolean;
    onStateUpdate: (updates: UpdateLiveSessionData) => void;
    onVideoEnd: () => void;
}

export function YouTubePlayer({ videoId, session, isAdmin, onStateUpdate, onVideoEnd }: YouTubePlayerProps) {
    const playerRef = useRef<YTPlayer | null>(null);
    const [isPlayerReady, setIsPlayerReady] = useState(false);
    // Track if the update is coming from local interaction or remote sync
    const isRemoteUpdate = useRef(false);

    // Sync tolerance in seconds (if difference is greater, seek)
    const SYNC_TOLERANCE = 2;

    // --- Sync Logic: Incoming Session Updates ---
    useEffect(() => {
        if (!playerRef.current || !isPlayerReady || isAdmin) return;

        const player = playerRef.current;
        const playerState = player.getPlayerState(); // 1 = playing, 2 = paused
        const currentTime = player.getCurrentTime();

        // 1. Sync Play/Pause
        if (session.isPlaying && playerState !== 1) {
            isRemoteUpdate.current = true;
            player.playVideo();
        } else if (!session.isPlaying && playerState !== 2) {
            isRemoteUpdate.current = true;
            player.pauseVideo();
        }

        // 2. Sync Time (Seek if drifted)
        // We calculate "expected time" based on lastUpdated timestamp if playing
        let expectedTime = session.currentTime;
        if (session.isPlaying) {
            const timeSinceUpdate = (new Date().getTime() - session.lastUpdated.getTime()) / 1000;
            expectedTime += timeSinceUpdate;
        }

        if (Math.abs(currentTime - expectedTime) > SYNC_TOLERANCE) {
            console.log(`Syncing time: local=${currentTime}, remote=${expectedTime}`);
            isRemoteUpdate.current = true;
            player.seekTo(expectedTime, true);
        }

    }, [session, isPlayerReady, isAdmin]);


    // --- Admin Logic: Outgoing State Updates ---
    const handleStateChange: YouTubeProps['onStateChange'] = async (event) => {
        if (!isAdmin) return;
        if (isRemoteUpdate.current) {
            isRemoteUpdate.current = false;
            return;
        }

        const player = event.target;
        const playerState = player.getPlayerState();
        const currentTime = player.getCurrentTime();

        if (playerState === 1) { // Playing
            onStateUpdate({
                isPlaying: true,
                currentTime: currentTime,
                lastUpdated: new Date() // Will be serverTimestamp() in service
            });
        } else if (playerState === 2) { // Paused
            onStateUpdate({
                isPlaying: false,
                currentTime: currentTime,
                lastUpdated: new Date()
            });
        }
    };

    // Periodic time sync for admin (optional, to keep server time somewhat fresh)
    useEffect(() => {
        if (!isAdmin || !isPlayerReady) return;

        const interval = setInterval(() => {
            const player = playerRef.current;
            if (player && player.getPlayerState() === 1) {
                onStateUpdate({
                    currentTime: player.getCurrentTime(),
                    isPlaying: true
                    // lastUpdated added by service
                });
            }
        }, 5000); // Update every 5s

        return () => clearInterval(interval);
    }, [isAdmin, isPlayerReady]);


    const onReady: YouTubeProps['onReady'] = (event) => {
        playerRef.current = event.target;
        setIsPlayerReady(true);
        if (isAdmin) {
            // If admin, maybe ensure we match session start state?
            // Actually, admin dictates state.
        } else {
            // Viewer creates, should mute by default to allow autoplay
            event.target.mute();
        }
    };

    const opts: YouTubeProps['opts'] = {
        height: '100%',
        width: '100%',
        playerVars: {
            autoplay: 1, // Try to autoplay
            controls: isAdmin ? 1 : 0, // Hide controls for viewers
            modestbranding: 1,
            rel: 0,
        },
    };

    return (
        <div className="ratio ratio-16x9 bg-black rounded overflow-hidden shadow">
            <YouTube
                videoId={videoId}
                opts={opts}
                onReady={onReady}
                onStateChange={handleStateChange} // For Admin state capture
                onEnd={onVideoEnd}
                className="w-100 h-100 position-absolute top-0 start-0"
            />
            {/* Overlay for viewers to prevent interaction? 
                Actually YouTube API controls=0 does a decent job, but an overlay ensures no clicking.
            */}
            {!isAdmin && (
                <div
                    className="position-absolute top-0 start-0 w-100 h-100"
                    style={{ zIndex: 10, opacity: 0 }} // Transparent overlay
                ></div>
            )}
        </div>
    );
}
